import React from 'react';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIsLinkingEnabled } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Placeholder } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/types/chart-item.tsx';
import { RoadmapDependencyHandlers } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-item-content/date-content/bar/bar-content/dependency-handler/index.tsx';
import { getColor } from '../../../../../common/utils/get-color.tsx';
import { useIssuesMap } from '../../../../../common/utils/use-issues-map.tsx';
import { useTimelineFeatures } from '../../../../../controllers/features-context/index.tsx';
import { useCreateDependency } from '../../../../../services/create-dependency/index.tsx';
import messages from './messages.tsx';

export type Props = {
	isHovered: boolean;
	leftPosition: number;
	rightPosition: number;
	getBarBoundingClientRect: () => ClientRect | undefined;
	id: string;
	placeholder: Placeholder;
	onBarMouseLeave: () => void;
};

export const DependencyHandler = (props: Props) => {
	const { id } = props;
	const { showFlag } = useFlagService();
	const [{ issuesMap }] = useIssuesMap();
	const isLinkingEnabled = useIsLinkingEnabled();
	const { hasIssueLinking } = useTimelineFeatures();
	const { create } = useCreateDependency();

	const issue = issuesMap.get(id);
	const color = getColor(issue?.status.categoryId);

	const onCreateDependency = (from: string, to: string) => {
		if (from !== to) {
			// @see JET-1624, JET-1626: we need to let the UI cleanup the drag state first
			requestAnimationFrame(() => {
				create({
					input: {
						inwardIssueId: toIssueId(to),
						outwardIssueId: toIssueId(from),
					},
				}).catch((createError) => {
					showFlag({
						type: 'error',
						title: messages.createDependencyErrorTitle,
						description:
							createError instanceof ValidationError
								? [messages.createDependencyDynamicErrorMessage, { error: createError.message }]
								: messages.createDependencyErrorMessage,
					});
				});
			});
		}
	};

	if (!hasIssueLinking || isLinkingEnabled !== true) {
		return null;
	}

	return (
		<RoadmapDependencyHandlers color={color} onCreateDependency={onCreateDependency} {...props} />
	);
};
